import { useId, useRef, useState, type ReactNode } from 'react';
import { MagnifyingGlassIcon, MapPinIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Switch } from '@voll-intelligence/ui-kit/ui/switch';
import {
  PlaceField,
  type PlaceFieldControlledSearch,
} from '../../shared/place_search/place_field.js';
import { MyLocationRow, type MyLocationState } from '../../shared/place_search/my_location_row.js';
import type { PlaceSuggestion } from '../../shared/place_search/place_panel.js';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type { RentalPlaceField, RentalSearchValue } from '../models.js';
import type { RentalDatesCopy } from './rental_dates_field.js';
import { FieldColumn } from './rental_field_column.js';
import { useGuidedFlow, type FieldGuide } from '../../shared/guided_flow.js';
import { guidedOpensOnMount, type GuidedOpen } from '../../shared/guided_open.js';
import {
  pendingRentalSearchFields,
  type RentalSearchPendingField,
} from './rental_search_pending.js';
import type { PersonItem } from '../../shared/person/person_row.js';
import type { TravelerFieldSearch } from '../../shared/person/who_search.js';
import { SwapButton } from '../../shared/route_controls.js';
import { RentalDriverField, type RentalDriverCopy } from './rental_driver_field.js';
import { RentalWhenFields } from './rental_when_fields.js';
import { FIELD_PILL_TOUCH_SCOPE_CLASS } from '../../shared/field_trigger.js';
import { isInvertedWindow, nowLocal, pickupFloorFrom } from './rental_window.js';
import './rental_search_bar.css';

/*
 * A barra de busca do rent a car no layout de PÁGINA — a entrada determinística
 * do #2441 (frame `Rent a car`, section Determinístico → Rental). De cima para
 * baixo: a cabeça, com o QUEM (o condutor, rótulo em cima, como no ônibus) e
 * ao lado o switch "Devolução no mesmo local" (#5641, o mesmo desenho do
 * cartão do chat); e os campos — o(s) lugar(es), com o inversor entre eles quando são
 * dois, e a janela da locação, com dias e horas no mesmo painel (#4513) — com o
 * Buscar fechando o bloco à direita.
 *
 * O QUEM é o seletor de pessoas compartilhado em modo único (`RentalDriverField`):
 * o condutor pode ser um colega, e quem está logado vira só o solicitante. Sem
 * busca do host ele é só leitura. A barra não tem a casca de card da conversa
 * (o palco do hotel `naked=false`).
 *
 * No palco ESTREITO (`NARROW_STAGE_QUERY`, o degrau de todas as superfícies
 * determinísticas) cada campo troca o popover por uma folha de tela cheia — a
 * do lugar é a do próprio `PlaceField`, a das datas é a `PageFieldSheet` da
 * página do hotel com o `DateSheetBody` dentro. O empilhamento dos campos segue por consulta de contêiner:
 * a folha responde à viewport porque é modal e cobre a tela, o layout responde
 * ao formulário porque é ele que encolhe no iframe do host.
 *
 * Mobile pattern (#5663): in the column (form below `sm`) the fields wear the
 * 44px touch pill (`FIELD_PILL_TOUCH_SCOPE_CLASS`) and the Buscar grows to 44
 * across the whole width. The QUEM stays in the head beside the switch on
 * every stage, like the other products.
 */

export type RentalSearchCopy = RentalDatesCopy & {
  /** O rótulo do campo QUEM ("Quem"), o mesmo dos outros campos. */
  driverLabel: string;
  /** A lista do QUEM. Ausente ⇒ o condutor é só leitura. */
  driverPicker?: RentalDriverCopy;
  /** O inversor entre retirada e devolução ("Inverter retirada e devolução"). */
  swapLabel: string;
  /** O switch da devolução ("Devolução no mesmo local"): ligado ⇒
   *  `dropoffMode: 'same'`; desligado revela o local de devolução. */
  sameDropoffLabel: string;
  /** O lugar único, quando retirada e devolução coincidem. */
  locationLabel: string;
  pickupLocationLabel: string;
  dropoffLocationLabel: string;
  locationPlaceholder: string;
  noPlacesLabel: string;
  placeErrorLabel: string;
  /** Ação "perto de mim" — primeira linha do painel de cada lugar. */
  myLocationLabel: string;
  /** Posição sem lugar / indisponível: dica na própria linha. */
  myLocationErrorLabel: string;
  /** Permissão recusada: a dica diz onde liberar (#4513, I6). Ausente ⇒ cai no
   *  `myLocationErrorLabel`. */
  myLocationDeniedLabel?: string;
  searchLabel: string;
  /** A seta de voltar das folhas do palco estreito ("Voltar"). */
  backLabel: string;
};

type FieldSheet = { readonly backLabel: string } | undefined;

export type RentalSearchBarProps = {
  value: RentalSearchValue;
  copy: RentalSearchCopy;
  /** Ausente ⇒ o QUEM não monta (o perfil ainda não chegou). */
  driver?: PersonItem;
  /** A busca do QUEM (`useTravelerFieldSearch`). Com ela e `onDriverChange` o
   *  condutor se escolhe; sem elas é só leitura. */
  driverSearch?: TravelerFieldSearch;
  onDriverChange?: (driver: PersonItem) => void;
  /** Os dois ciclos de busca de lugar, controlados pelo host — um por campo,
   *  porque os dois podem estar abertos em sequência com termos diferentes. */
  pickupSearch: PlaceFieldControlledSearch<PlaceSuggestion>;
  dropoffSearch: PlaceFieldControlledSearch<PlaceSuggestion>;
  busy?: boolean;
  /** Veto do host por cima da completude (com campo pendente a barra já
   *  segura a busca: no guia o Buscar pergunta o campo, fora dele trava). */
  submitDisabled?: boolean;
  onChange: (value: RentalSearchValue) => void;
  /** A busca, com o valor que ela leva — do Buscar ou do Confirmar do
   *  período no preenchimento guiado, que sai antes do commit do `onChange`. */
  onSubmit: (value: RentalSearchValue) => void;
  /** Estado do pedido de posição do host e QUAL campo o fez: o spinner e o
   *  erro aparecem só onde a pessoa clicou. Ausente junto de `onUseMyLocation`
   *  ⇒ a linha "perto de mim" não existe. */
  myLocation?: { state: MyLocationState; field: RentalPlaceField };
  /** O clique em "perto de mim" no campo dado: o host lê a posição, troca-a por
   *  um lugar real e grava o campo com `source: 'geolocation'` — a barra não
   *  sabe de geolocation; um pick dela grava o lugar sem origem. */
  onUseMyLocation?: (field: RentalPlaceField) => void;
  /** O preenchimento GUIADO da página determinística (ver `useGuidedFlow`):
   *  o primeiro campo vazio pede a vez (o anel de pendente) e responder um
   *  campo abre o próximo pendente — lugar(es) → período — em popover na
   *  fileira larga e em folha no palco estreito; confirmar o período com tudo
   *  respondido BUSCA ("Confirmar e buscar"), e escolher o último lugar com o
   *  período já dado só leva o foco ao Buscar. Na fileira larga o primeiro
   *  campo pendente já nasce aberto; fora disso só a continuação de um gesto
   *  abre, e alternar o switch da devolução só move o anel. Ausente ⇒ nada
   *  disso. */
  guided?: boolean;
  /** Em que palco o guia abre o primeiro pendente ao nascer (`GuidedOpen`):
   *  a página fica no `wide`; o cartão da conversa pede `always`, e o
   *  remontado do histórico, `never`. */
  guidedOpen?: GuidedOpen;
  /** Um lugar que a pessoa disse e ninguém resolveu ainda (#5641): o campo
   *  vazio mostra o termo e abre buscando por ele — o lugar continua sendo
   *  escolhido na lista. Ver `PlaceField.draft`. */
  placeDrafts?: { readonly pickup?: string; readonly dropoff?: string };
  /** O campo que nasce ABERTO na montagem: a volta pela pílula dos resultados
   *  abre o que a pessoa clicou (#4513, I8). Lido só na montagem. */
  openField?: 'place' | 'dates';
};

/** O nome acessível vai por `aria-labelledby`, como no `SwitchFilterRow`: o
 *  Base UI desenha o switch como `<span role="switch">`, e o `<label>` em volta
 *  só nomeia o `<input>` escondido dele (#3217 C10). */
function SameDropoffSwitch({
  label,
  checked,
  disabled,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (checked: boolean) => void;
}): ReactNode {
  const labelId = useId();
  return (
    <label className="text-ui inline-flex h-9 cursor-pointer items-center gap-2 px-3">
      <Switch
        checked={checked}
        disabled={disabled}
        aria-labelledby={labelId}
        onCheckedChange={onChange}
      />
      <span id={labelId}>{label}</span>
    </label>
  );
}

function PlaceColumn({
  label,
  placeholder,
  copy,
  value,
  search,
  disabled,
  sheet,
  draft,
  guide,
  myLocationState,
  onUseMyLocation,
  onPick,
}: {
  readonly label: string;
  readonly placeholder: string;
  readonly copy: RentalSearchCopy;
  readonly value: RentalSearchValue['pickupPlace'];
  readonly search: PlaceFieldControlledSearch<PlaceSuggestion>;
  readonly disabled: boolean;
  readonly sheet: FieldSheet;
  /** Ver `RentalSearchBarProps.placeDrafts` — já no formato do `PlaceField`. */
  readonly draft: { readonly draft?: string };
  /** Ver `useGuidedFlow` — o anel e a abertura por continuação de gesto. */
  readonly guide: FieldGuide;
  /** O estado do pedido DESTE campo — o do outro fica `idle`. */
  readonly myLocationState: MyLocationState;
  readonly onUseMyLocation: (() => void) | undefined;
  readonly onPick: (place: PlaceSuggestion) => void;
}): ReactNode {
  /* Abertura controlada AQUI, e não pelo próprio `PlaceField`, por causa do
     "perto de mim": o lugar chega de FORA (o host troca a posição por um lugar
     e grava o valor), e o campo tem de fechar quando ele chega, como fecha num
     pick — enquanto a posição carrega, segue aberto com o spinner na linha.
     Derivado, não em effect: a abertura vale para o valor em que foi pedida;
     valor novo ⇒ fechado. */
  const [opened, setOpened] = useState<{ open: boolean; forId: string | undefined }>({
    // Nascido com o pedido do guia (a página larga): já aberto.
    open: guide.advance,
    forId: value?.id,
  });
  /* O `forId` fecha no lugar NOVO, mas a posição pode resolver para o lugar que
     JÁ estava escolhido: o id não muda e o campo ficaria aberto depois de um
     pedido bem-sucedido. Por isso o fim do pedido também fecha — comparação com
     o render anterior (padrão de estado derivado do React), não um effect. */
  const locating = myLocationState === 'loading';
  const [wasLocating, setWasLocating] = useState(locating);
  if (wasLocating !== locating) {
    setWasLocating(locating);
    // Só o SUCESSO fecha: `error` mantém a lista aberta com a dica na linha.
    if (wasLocating && myLocationState === 'idle') setOpened({ open: false, forId: value?.id });
  }
  /* O pedido do guia (`advance`) abre o campo na BORDA falso→verdadeiro — o
     mesmo padrão derivado dos dois acima, e não um effect: abrir é resposta ao
     pedido que chegou neste render, e um render depois ele já foi consumido. */
  const [sawAdvance, setSawAdvance] = useState(guide.advance);
  if (sawAdvance !== guide.advance) {
    setSawAdvance(guide.advance);
    if (guide.advance) setOpened({ open: true, forId: value?.id });
  }
  const open = opened.forId === value?.id && opened.open;
  const setOpen = (next: boolean) => {
    setOpened({ open: next, forId: value?.id });
    // Fechou: o pedido do guia que o abriu já foi atendido. O pick do campo
    // fecha ANTES de gravar, então um pedido novo nascido do pick sobrevive.
    if (!next) guide.onSettle();
  };
  return (
    <FieldColumn label={label} field="place">
      <PlaceField<PlaceSuggestion>
        label={label}
        {...(value ? { value: { title: value.label }, selectedPlaceId: value.id } : {})}
        emptyText={placeholder}
        icon={<MapPinIcon className="size-3.5" aria-hidden="true" />}
        variant="outline"
        disabled={disabled}
        copy={{ label, emptyLabel: copy.noPlacesLabel, errorLabel: copy.placeErrorLabel }}
        search={search}
        {...(sheet ? { sheet } : {})}
        {...draft}
        wanted={guide.wanted}
        open={open}
        // Aberto pelo guia (continuação do pick anterior): o input nasce focado.
        openedByGesture={guide.advance}
        onOpenChange={setOpen}
        header={
          <MyLocationRow
            copy={{
              label: copy.myLocationLabel,
              errorLabel: copy.myLocationErrorLabel,
              ...(copy.myLocationDeniedLabel ? { deniedLabel: copy.myLocationDeniedLabel } : {}),
            }}
            state={myLocationState}
            {...(onUseMyLocation ? { onUse: onUseMyLocation } : {})}
          />
        }
        onPick={(place) => {
          // Sob abertura controlada o `PlaceField` não fecha sozinho no pick.
          setOpen(false);
          onPick(place);
        }}
      />
    </FieldColumn>
  );
}

/** O QUEM na cabeça, ao lado do switch, com o rótulo em cima como no ônibus.
 *  Outside the touch scope: the 44px pill pushes the switch onto a line of its
 *  own on a 390px phone. Sem condutor (o perfil não chegou) não monta. */
function RentalWhoRow({
  props,
  disabled,
}: {
  readonly props: RentalSearchBarProps;
  readonly disabled: boolean;
}): ReactNode {
  const { driver, copy } = props;
  if (!driver) return null;
  return (
    <div className="iuk-rental-search-who">
      <span aria-hidden="true" className="iuk-rental-search-label">
        {copy.driverLabel}
      </span>
      <RentalDriverField
        driver={driver}
        label={copy.driverLabel}
        {...(props.driverSearch ? { search: props.driverSearch } : {})}
        {...(copy.driverPicker ? { copy: copy.driverPicker } : {})}
        disabled={disabled}
        {...(props.onDriverChange ? { onChange: props.onDriverChange } : {})}
      />
    </div>
  );
}

/** Retirada e devolução trocadas — o inversor do ônibus, para os dois lugares. */
function swapPlaces(value: RentalSearchValue): RentalSearchValue {
  const { pickupPlace, dropoffPlace, ...rest } = value;
  return {
    ...rest,
    ...(dropoffPlace ? { pickupPlace: dropoffPlace } : {}),
    ...(pickupPlace ? { dropoffPlace: pickupPlace } : {}),
  };
}

/** O rascunho de um campo de lugar, no formato opcional do `PlaceField`. */
function draftOf(term: string | undefined): { readonly draft?: string } {
  return term ? { draft: term } : {};
}

/** O período responde as DUAS datas: só a devolução pendente (um valor do host
 *  com a retirada e sem a volta) também é vez dele. */
function isDatesTurn(field: RentalSearchPendingField): boolean {
  return field === 'pickupAt' || field === 'dropoffAt';
}

/**
 * A busca bloqueada para `value`: campo pendente (no guia o Buscar o pergunta,
 * ver `searchCta`), veto do host ou uma janela que não é locação. Cinto por
 * cima dos pisos: um valor que chegue pelo host (URL, pedido antigo) invertido
 * ou já no passado não pode sair como busca — o painel não confirma janela
 * invertida, mas não sanitiza o que não passou por ele.
 */
function blocked(value: RentalSearchValue, vetoed: boolean, pickupFloor: string): boolean {
  return vetoed || pendingRentalSearchFields(value).length > 0 || invalidWindow(value, pickupFloor);
}

/** A janela do host que não é locação: invertida ou com a retirada vencida. */
function invalidWindow(value: RentalSearchValue, pickupFloor: string): boolean {
  const expiredPickup = value.pickupAt !== undefined && value.pickupAt < pickupFloor;
  return isInvertedWindow(value) || expiredPickup;
}

export function RentalSearchBar(props: RentalSearchBarProps): ReactNode {
  const { value, copy, onChange } = props;
  const disabled = props.busy ?? false;
  const different = value.dropoffMode === 'different';
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  const sheet: FieldSheet = narrow ? { backLabel: copy.backLabel } : undefined;
  const pickupFloor = pickupFloorFrom(nowLocal());
  const searchRef = useRef<HTMLButtonElement>(null);
  const guide = useGuidedFlow({
    guided: props.guided === true && !disabled,
    value,
    pending: pendingRentalSearchFields,
    opensOnMount: guidedOpensOnMount(props.guidedOpen ?? 'wide', narrow),
    submitRef: searchRef,
    vetoed: props.submitDisabled ?? false,
    canSubmit: (next) => !blocked(next, props.submitDisabled ?? false, pickupFloor),
    onSubmit: props.onSubmit,
  });
  // O pedido de abertura da montagem vira o mesmo `advance` do guia: o campo
  // já sabe nascer aberto por ele, e um `advance` constante nunca reabre.
  const [openOnMount] = useState(props.openField);
  const opening = (field: 'place' | 'dates', fieldGuide: FieldGuide): FieldGuide =>
    openOnMount === field ? { ...fieldGuide, advance: true } : fieldGuide;
  const placeGuide = (field: 'pickupPlace' | 'dropoffPlace'): FieldGuide =>
    field === 'pickupPlace'
      ? opening(
          'place',
          guide.fieldGuide((pending) => pending === field),
        )
      : guide.fieldGuide((pending) => pending === field);
  /** Um lugar respondido: grava e, no guia, segue pro próximo pendente. */
  const answer = (next: RentalSearchValue) => {
    onChange(next);
    guide.answer(next);
  };
  const myLocationStateOf = (field: RentalPlaceField): MyLocationState =>
    props.myLocation?.field === field ? props.myLocation.state : 'idle';
  const myLocationHandlerFor = (field: RentalPlaceField): (() => void) | undefined =>
    props.onUseMyLocation ? () => props.onUseMyLocation?.(field) : undefined;
  const search = guide.searchCta(
    disabled || blocked(value, props.submitDisabled ?? false, pickupFloor),
    () => {
      props.onSubmit(value);
    },
    // A janela inválida trava mesmo com campo vazio: responder o campo não a
    // corrigiria, e o Buscar só levaria a pessoa a outro Buscar travado.
    invalidWindow(value, pickupFloor) ? [] : undefined,
  );

  return (
    <div className="iuk-rental-search">
      <div className="iuk-rental-search-head">
        <RentalWhoRow props={props} disabled={disabled} />
        <SameDropoffSwitch
          label={copy.sameDropoffLabel}
          checked={!different}
          disabled={disabled}
          onChange={(same) => {
            onChange({ ...value, dropoffMode: same ? 'same' : 'different' });
          }}
        />
      </div>
      <div className={`iuk-rental-search-row ${FIELD_PILL_TOUCH_SCOPE_CLASS}`}>
        <PlaceColumn
          label={different ? copy.pickupLocationLabel : copy.locationLabel}
          placeholder={copy.locationPlaceholder}
          copy={copy}
          value={value.pickupPlace}
          search={props.pickupSearch}
          disabled={disabled}
          sheet={sheet}
          draft={draftOf(props.placeDrafts?.pickup)}
          guide={placeGuide('pickupPlace')}
          myLocationState={myLocationStateOf('pickup')}
          onUseMyLocation={myLocationHandlerFor('pickup')}
          onPick={(place) => {
            answer({ ...value, pickupPlace: { id: place.id, label: place.label } });
          }}
        />
        {different && (
          <span className="iuk-rental-search-swap">
            <SwapButton
              label={copy.swapLabel}
              disabled={disabled}
              onSwap={() => {
                onChange(swapPlaces(value));
              }}
            />
          </span>
        )}
        {different && (
          <PlaceColumn
            label={copy.dropoffLocationLabel}
            placeholder={copy.locationPlaceholder}
            copy={copy}
            value={value.dropoffPlace}
            search={props.dropoffSearch}
            disabled={disabled}
            sheet={sheet}
            draft={draftOf(props.placeDrafts?.dropoff)}
            guide={placeGuide('dropoffPlace')}
            myLocationState={myLocationStateOf('dropoff')}
            onUseMyLocation={myLocationHandlerFor('dropoff')}
            onPick={(place) => {
              answer({ ...value, dropoffPlace: { id: place.id, label: place.label } });
            }}
          />
        )}
        <RentalWhenFields
          value={value}
          copy={copy}
          disabled={disabled}
          sheet={sheet}
          pickupFloor={pickupFloor}
          datesGuide={opening('dates', guide.fieldGuide(isDatesTurn))}
          datesSearch={guide.confirmSearches(isDatesTurn)}
          onChange={onChange}
          onDatesConfirmed={guide.confirm}
        />
      </div>
      <div className="iuk-rental-search-actions">
        <Button
          ref={searchRef}
          size="lg"
          // The `xl` size of the DS below `sm`: the size prop cannot read a container.
          className="rounded-full px-4 @max-[theme(--breakpoint-sm)]:h-11 @max-[theme(--breakpoint-sm)]:gap-2 @max-[theme(--breakpoint-sm)]:text-[0.9375rem]"
          {...search}
        >
          <MagnifyingGlassIcon />
          {copy.searchLabel}
        </Button>
      </div>
    </div>
  );
}
